import PropTypes from 'prop-types';
import { useContext } from 'react';
import Link from 'next/link';
import cn from 'class-names';

import { StateContext } from '@/context/index';

import { isWHEnv, isDDEnv } from '@/utils/helper';

import Icon from '@/atoms/Icon/Icon';
import Text from '@/atoms/Text/Text';

import { normalizedUrl } from './CTA.constants';
import { ICON_POSITION, getIconClass } from '@/atoms/Icon/Icon.constants';
import { getButtonClass } from '@/atoms/Button/Button.constants';
import { CtfCtaStyle } from '@/utils/constants';

export function CtaWrapper({ url, page, children, classNames, name, onClick, reloadPage }) {
  const { environment } = useContext(StateContext);

  const isLocal = !url?.startsWith('#') && !url?.startsWith('http');
  if (isLocal) {
    url = normalizedUrl(page ? page : url);
    if (environment) {
      url = `/${environment}${url}`;
    }

    return (
      <Link href={url} {...(isDDEnv() && { locale: false })}>
        <a
          className={`aaco-cta inline-flex items-center ${classNames ?? ''}`}
          onClick={(e) => {
            if(reloadPage) {
              return window.location.href = url;
            }
            if (onClick) {
              onClick(e, { localUrl: url, name });
            }
          }}
        >
          {children}
        </a>
      </Link>
    );
  }

  const anchor = url?.startsWith('#');
  return (
    <a
      className={`aaco-cta flex items-center ${classNames ?? ''}`}
      href={url}
      target={anchor ? '_self' : ''}
      rel="noopener noreferrer"
      onClick={(e) => {
        if (onClick) {
          onClick(e, { url: !anchor && url, localUrl: anchor && url, name });
        }
      }}
    >
      {children}
    </a>
  );
}

export function Cta({
  iconProps = {},
  buttonProps = {},
  linkProps = {},
  onClick,
  isWhiteCta,
} = {}) {
  const { invert, name, type, style: buttonStyle, size = "large" } = buttonProps;
  const { url, page, reloadPage } = linkProps;

  const {
    hasIconBorder,
    iconPosition = ICON_POSITION.right,
    height,
    hideIcon,
    name: icon,
    style: iconStyle,
    width,
  } = iconProps;

  const iconButtonClass = getIconClass({
    icon,
    style: iconStyle,
    iconPosition,
    invert,
    hasIconBorder: isWHEnv() ? true : hasIconBorder,
  });

  const iconTextClass = getButtonClass({
    type,
    invert,
    style: buttonStyle,
  });

  const isDD = isDDEnv();

  const getCtaWrapperClass = () => {
    let typeClass = '';
    switch (type) {
      case CtfCtaStyle.DD_PRIMARY_FILLED:
        typeClass = 'aaco-cta__primary-filled';
        break;
      case CtfCtaStyle.DD_PRIMARY_LINE:
        typeClass = 'aaco-cta__primary-line';
        break;
      case CtfCtaStyle.DD_SECONDARY:
        typeClass = 'aaco-cta__secondary';
        break;
      case CtfCtaStyle.DD_TERTIARY:
        typeClass = 'aaco-cta__tertiary';
        break;
      case CtfCtaStyle.PRIMARY:
        typeClass = isDD ? 'aaco-cta__secondary' : `aaco-cta__primary-wh ${size === "large" ? "md:py-2 md:px-4" : "md:py-1 md:px-2"} px-2 py-1`;
        break;
      default:
        break;
    }
    const invertClass = invert ? 'aaco-cta--invert link-invert' : '';
    return [typeClass, invertClass].join(' ');
  };

  const showIcon = !hideIcon && type !== CtfCtaStyle.SECONDARY && icon;
  const textSize = type === CtfCtaStyle.PRIMARY && size === "large" && !isDD ? 'button-wh-primary' : 'button';
  return (
    <CtaWrapper
      url={url}
      page={page}
      classNames={cn(
        getCtaWrapperClass(),
        {
          'aaco-cta__primary-wh-white': isWhiteCta
        }
      )}
      onClick={onClick}
      name={name}
      reloadPage={reloadPage}
    >
      {isDDEnv() && showIcon && iconPosition === ICON_POSITION.left && (
        <div className={iconButtonClass}>
          <Icon
            name={icon}
            className={iconStyle}
            height={height}
            width={width}
            hasIconBorder={hasIconBorder}
          />
        </div>
      )}
      <Text tag={textSize} style={iconTextClass}>
        {name}
      </Text>
      {isDDEnv() && showIcon && iconPosition === ICON_POSITION.right && (
        <div className={iconButtonClass}>
          <Icon
            name={icon}
            className={iconStyle}
            width={width}
            height={height}
            hasIconBorder={hasIconBorder}
          />
        </div>
      )}
    </CtaWrapper>
  );
}

Cta.propTypes = {
  url: PropTypes.string,
  height: PropTypes.number,
  page: PropTypes.string,
  reloadPage: PropTypes.bool,
  name: PropTypes.string,
  type: PropTypes.string,
  icon: PropTypes.string,
  iconStyle: PropTypes.string,
  iconPosition: PropTypes.string,
  invert: PropTypes.bool,
  style: PropTypes.string,
  width: PropTypes.number,
  onClick: PropTypes.func,
};

Cta.defaultProps = {
  type: 'Primary',
  iconPosition: ICON_POSITION.right,
  iconStyle: '',
  invert: false,
};
